import { useState, useRef, useMemo, useCallback, memo } from 'react';
import {
  CartesianGrid,
  XAxis,
  YAxis,
  ComposedChart,
  ResponsiveContainer,
  Area,
  Line,
  TooltipContentProps,
  Brush,
  Bar,
  Tooltip,
} from 'recharts';
import { Button } from '@/components/ui/button.tsx';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { useBrushSelection } from '@/hooks/use-brush-selection.tsx';
import { RefreshCwIcon } from 'lucide-react';
import { format, isThisYear, isToday, isTomorrow, Locale } from 'date-fns';
import { enUS, ro } from 'date-fns/locale';

import type {
  Payload,
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import { calculateDateDifference } from '@/utils/compare-dates.ts';
import { cn } from '@/utils/utils.ts';
import { useTranslation } from 'react-i18next';

const METRIC_COLORS = {
  PV_PRODUCTION: '#18CF87',
  CONSUMED: '#F76354',
  CONSUMED_FROM_GRID: '#EEBA18',
  FED_TO_GRID: '#8DD76B',
  BATTERY_CHARGE: '#038BA8',
  BATTERY_DISCHARGE: '#AC44FB',
  BATTERY_STATE_OF_CHARGE: '#AC99FB',
  ROPEX_DAM_PRICE: '#D97706',
} as const;

const METRIC_KEYS = {
  PV_PRODUCTION: 'pv_production',
  CONSUMED: 'consumed',
  CONSUMED_FROM_GRID: 'consumed_from_grid',
  FED_TO_GRID: 'fed_to_grid',
  BATTERY_CHARGE: 'battery_charge',
  BATTERY_DISCHARGE: 'battery_discharge',
  BATTERY_STATE_OF_CHARGE: 'battery_state_of_charge',
  ROPEX_DAM_PRICE: 'ropex_dam_price',
} as const;

const CHART_METRICS = [
  {
    label: 'PV Production',
    color: METRIC_COLORS.PV_PRODUCTION,
    selected: true,
    dataKey: METRIC_KEYS.PV_PRODUCTION,
    axis: true,
    id: METRIC_KEYS.PV_PRODUCTION,
  },
  {
    label: 'Consumed',
    color: METRIC_COLORS.CONSUMED,
    selected: true,
    dataKey: METRIC_KEYS.CONSUMED,
    axis: false,
    id: METRIC_KEYS.CONSUMED,
  },
  {
    label: 'Consumed from Grid',
    color: METRIC_COLORS.CONSUMED_FROM_GRID,
    selected: true,
    dataKey: METRIC_KEYS.CONSUMED_FROM_GRID,
    axis: false,
    id: METRIC_KEYS.CONSUMED_FROM_GRID,
  },
  {
    label: 'Fed to Grid',
    color: METRIC_COLORS.FED_TO_GRID,
    selected: true,
    dataKey: METRIC_KEYS.FED_TO_GRID,
    axis: false,
    id: METRIC_KEYS.FED_TO_GRID,
  },
  {
    label: 'Battery Charge',
    color: METRIC_COLORS.BATTERY_CHARGE,
    selected: true,
    dataKey: METRIC_KEYS.BATTERY_CHARGE,
    axis: false,
    id: METRIC_KEYS.BATTERY_CHARGE,
  },
  {
    label: 'Battery Discharge',
    color: METRIC_COLORS.BATTERY_DISCHARGE,
    selected: true,
    dataKey: METRIC_KEYS.BATTERY_DISCHARGE,
    axis: false,
    id: METRIC_KEYS.BATTERY_DISCHARGE,
  },
  {
    label: 'Battery State of Charge',
    color: METRIC_COLORS.BATTERY_STATE_OF_CHARGE,
    selected: true,
    dataKey: METRIC_KEYS.BATTERY_STATE_OF_CHARGE,
    axis: false,
    id: METRIC_KEYS.BATTERY_STATE_OF_CHARGE,
  },
  {
    label: 'ROPEX DAM Price',
    color: METRIC_COLORS.ROPEX_DAM_PRICE,
    selected: true,
    dataKey: METRIC_KEYS.ROPEX_DAM_PRICE,
    axis: false,
    id: METRIC_KEYS.ROPEX_DAM_PRICE,
  },
];

export type TEnergyManagementRecord = {
  Battery_Charge: number;
  Battery_Discharge: number;
  Battery_State_of_Charge?: number;
  Consumed: number;
  Consumed_from_Grid: number;
  Fed_to_Grid: number;
  Yield: number;
  ROPEX_DAM_Price?: number;
  time: string;
};

import type { RangeGranularity } from '@/utils/granularity.ts';

export type MetricKey = (typeof METRIC_KEYS)[keyof typeof METRIC_KEYS];

type ZoomableChartProps = {
  data?: TEnergyManagementRecord[];
  rangeGranularity: RangeGranularity;
  currentViewContext?: 'all' | string;
  className?: string;
  hiddenMetrics?: MetricKey[];
};

const shouldUseBars = (granularity: RangeGranularity): boolean => {
  return ['hourly', 'daily', 'monthly'].includes(granularity);
};

const shouldShowBatterySoC = ({
  currentViewContext,
  rangeGranularity,
}: {
  currentViewContext?: 'all' | string;
  rangeGranularity: RangeGranularity;
}) => {
  return (
    currentViewContext !== 'all' &&
    (rangeGranularity === '1min' ||
      rangeGranularity === '5min' ||
      rangeGranularity === '15min' ||
      rangeGranularity === 'hourly')
  );
};

export const EnergyManagementChart = memo(function EnergyManagementChart({
  data,
  rangeGranularity,
  currentViewContext,
  className,
  hiddenMetrics = [],
}: ZoomableChartProps) {
  const { t, i18n } = useTranslation(['translation'], { keyPrefix: 'metrics' });
  const isMobile = useMediaQuery('(max-width:640px)');
  const chartRef = useRef<HTMLDivElement>(null);

  const [selectedState, setSelectedState] = useState<Record<string, boolean>>({});
  const { brushState, handleBrushChange } = useBrushSelection(data);

  const fields = useMemo(
    () =>
      CHART_METRICS.filter((metric) => !hiddenMetrics.includes(metric.dataKey)).map(
        (metric) => ({
          ...metric,
          selected: selectedState[metric.id] ?? true,
        })
      ),
    [hiddenMetrics, selectedState]
  );

  const locale = (i18n.language as 'ro' | 'en') === 'ro' ? ro : enUS;

  const translatedFields = useMemo(() => {
    return fields.map((field) => ({
      ...field,
      label: t(field.id),
    }));
  }, [t, fields]);

  const chartDate = useMemo(() => {
    if (!data) return [];

    const processValue = (value: number | null, shouldNegate = false): number | null => {
      if (value === null) return null;
      const absValue = Math.abs(value);
      return shouldNegate && shouldUseBars(rangeGranularity) ? -absValue : absValue;
    };

    const formatRecord = (v: TEnergyManagementRecord) => ({
      [METRIC_KEYS.PV_PRODUCTION]: processValue(v?.Yield),
      [METRIC_KEYS.FED_TO_GRID]: processValue(v?.Fed_to_Grid, true),
      [METRIC_KEYS.CONSUMED]: processValue(v?.Consumed),
      [METRIC_KEYS.CONSUMED_FROM_GRID]: processValue(v?.Consumed_from_Grid),
      [METRIC_KEYS.BATTERY_CHARGE]: processValue(v?.Battery_Charge),
      [METRIC_KEYS.BATTERY_DISCHARGE]: processValue(v?.Battery_Discharge, true),
      [METRIC_KEYS.BATTERY_STATE_OF_CHARGE]: v?.Battery_State_of_Charge ?? null,
      [METRIC_KEYS.ROPEX_DAM_PRICE]: v?.ROPEX_DAM_Price ?? null,
      time: v?.time,
    });

    return data.map((v) => {
      return formatRecord(v);
    });
  }, [data, rangeGranularity]);

  const dateDifference = useMemo(
    () =>
      calculateDateDifference(
        new Date(data?.at(0)?.time ?? ''),
        new Date(data?.at(-1)?.time ?? '')
      ),
    [data]
  );

  const isSoCVisible = useMemo(
    () => shouldShowBatterySoC({ currentViewContext, rangeGranularity }),
    [currentViewContext, rangeGranularity]
  );

  const isRopexVisible = rangeGranularity === '15min';

  const filteredFields = useMemo(
    () =>
      translatedFields.filter((f) => {
        if (f.id === 'battery_state_of_charge' && !isSoCVisible) return false;
        if (f.id === 'ropex_dam_price' && !isRopexVisible) return false;
        return true;
      }),
    [translatedFields, isSoCVisible, isRopexVisible]
  );

  const chartMargin = useMemo(
    () => ({
      top: 4,
      right: isMobile ? (isSoCVisible ? -24 : -60) : isSoCVisible ? -18 : -35,
      bottom: 0,
      left: isMobile ? -15 : 0,
    }),
    [isMobile, isSoCVisible]
  );

  const xAxisTickFormatter = useCallback(
    (t: string) => formatDate(new Date(t), rangeGranularity, dateDifference, locale),
    [rangeGranularity, dateDifference, locale]
  );

  const brushTickFormatter = useCallback(
    (value: string) => {
      if (!value) return '';
      return formatDate(new Date(value), rangeGranularity, dateDifference, locale);
    },
    [rangeGranularity, dateDifference, locale]
  );

  const handleResetSelection = useCallback(() => {
    setSelectedState({});
  }, []);

  return (
    <div className={cn(className, 'flex w-full flex-col gap-2 select-none')}>
      <div className={cn(`relative w-full`, isMobile ? 'h-[380px]' : 'h-[420px]')}>
        <div className="absolute top-3 right-12 z-10"></div>
        <div className="h-full w-full">
          <div className="h-full" ref={chartRef}>
            <div className="absolute top-3 right-3 z-10 flex justify-end sm:mb-4">
              <Button
                variant="outline"
                onClick={handleResetSelection}
                className="m-0 size-8 p-0 text-sm hover:bg-white"
              >
                <RefreshCwIcon className="size-5 stroke-[1.8]" />
              </Button>
            </div>
            <ResponsiveContainer width="100%" height="100%" debounce={100}>
              <ComposedChart data={chartDate} margin={chartMargin}>
                <defs>
                  {fields.map((v) => (
                    <linearGradient key={v.id} id={v.id} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={v.color} stopOpacity={1} />
                      <stop offset="95%" stopColor={v.color} stopOpacity={0.2} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid vertical={false} stroke="#e5e7eb" />
                <XAxis
                  key="xAxis-main"
                  dataKey="time"
                  xAxisId="main"
                  minTickGap={40}
                  tickFormatter={xAxisTickFormatter}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                />
                <YAxis
                  yAxisId={0}
                  label={{
                    value:
                      rangeGranularity === '5min' || rangeGranularity === '15min'
                        ? 'kW'
                        : 'kWh',
                    angle: -90,
                    position: 'left',
                    offset: isMobile ? -20 : -10,
                  }}
                  style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={{
                    value: '%',
                    angle: -90,
                    position: 'right',
                    offset: isMobile ? -30 : -25,
                    fontSize: isMobile ? 12 : 16,
                  }}
                  style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                />
                <YAxis
                  yAxisId="price"
                  hide
                  domain={[
                    (min) => min - Math.abs(min) * 0.1,
                    (max) => max + Math.abs(max) * 0.1,
                  ]}
                />
                <Tooltip
                  cursor={false}
                  content={(props) => (
                    <ChartTooltipComponent
                      {...props}
                      dateDifference={dateDifference}
                      rangeGranularity={rangeGranularity}
                      locale={locale}
                    />
                  )}
                />

                <Brush
                  dataKey="time"
                  height={18}
                  startIndex={brushState.startIndex}
                  endIndex={brushState.endIndex}
                  stroke="hsl(var(--chart-1))"
                  fill="hsl(var(--chart-5))"
                  tickFormatter={brushTickFormatter}
                  onChange={handleBrushChange}
                />

                {fields.map((v, index) => {
                  if (!v.selected) return null;

                  if (v.dataKey === 'ropex_dam_price') {
                    return (
                      <Line
                        xAxisId="main"
                        yAxisId="price"
                        name={t ? t(v.dataKey) : v.label}
                        key={`line-${v.dataKey}-${index}`}
                        dataKey={v.dataKey}
                        stroke={v.color}
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    );
                  }

                  const hasOneRecord = isMobile
                    ? chartDate?.length <= 4
                    : chartDate?.length <= 7;

                  if (shouldUseBars(rangeGranularity)) {
                    if (v.dataKey === 'battery_state_of_charge') {
                      return (
                        <Area
                          xAxisId="main"
                          yAxisId={'right'}
                          name={t ? t(v.dataKey) : v.label}
                          key={`area-${v.dataKey}-${index}`}
                          dataKey={v.dataKey}
                          stroke={v.color}
                          fill={`url(#${v.id})`}
                          fillOpacity={0.3}
                        />
                      );
                    }

                    return [
                      hasOneRecord ? null : (
                        <XAxis key={`xAxis-${index}`} dataKey="time" xAxisId={index} hide />
                      ),
                      <Bar
                        key={`bar-${v.dataKey}-${index}`}
                        xAxisId={hasOneRecord ? 'main' : index}
                        yAxisId={0}
                        dataKey={v.dataKey}
                        name={t ? t(v.dataKey) : v.label}
                        fill={`${v.color}80`}
                        stroke={v.color}
                        strokeWidth={1}
                        radius={[10, 10, 0, 0]}
                      />,
                    ];
                  } else {
                    return (
                      <Area
                        xAxisId="main"
                        yAxisId={v.id === 'battery_state_of_charge' ? 'right' : 0}
                        name={t ? t(v.dataKey) : v.label}
                        key={`area-${v.dataKey}-${index}`}
                        dataKey={v.dataKey}
                        stroke={v.color}
                        fill={`url(#${v.id})`}
                        fillOpacity={0.3}
                      />
                    );
                  }
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="mx-auto flex flex-wrap gap-1 md:gap-1">
        {filteredFields.map((f) => {
          return (
            <Button
              key={f.dataKey}
              variant="ghost"
              style={{ opacity: !f.selected ? '0.4' : '1' }}
              className="h-min w-min justify-start p-1 px-2"
              onClick={() => {
                setSelectedState((current) => ({
                  ...current,
                  [f.id]: !f.selected,
                }));
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="size-4 rounded-full"
                  style={{ backgroundColor: f.color }}
                ></span>
                <span className="text-start text-sm font-medium text-nowrap text-[#7D7D85]">
                  {f.label}
                </span>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
});

const ChartTooltipComponent = ({
  payload,
  rangeGranularity,
  dateDifference,
  locale,
}: TooltipContentProps<ValueType, NameType> & {
  rangeGranularity: '1min' | '5min' | '15min' | 'hourly' | 'daily' | 'monthly' | undefined;
  dateDifference: {
    days: number;
    weeks: number;
    months: number;
    years: number;
  };
  locale: Locale;
}) => {
  const time = payload?.[0]?.payload?.time;

  let dateForFormatting: Date | undefined;
  if (typeof time === 'string' || typeof time === 'number') {
    const d = new Date(time);
    if (!isNaN(d.getTime())) dateForFormatting = d;
  }

  const formattedDate = dateForFormatting
    ? formatDate(dateForFormatting, rangeGranularity, dateDifference, locale)
    : '';

  const formatValue = (p: Payload<ValueType, NameType>): string => {
    if (p?.dataKey && p.dataKey === 'battery_state_of_charge') return `${p.value} %`;
    if (p?.dataKey && p.dataKey === 'ropex_dam_price')
      return `${Number(p.value ?? 0).toFixed(2)} RON/MWh`;

    if (!p?.value && rangeGranularity === '1min') return '0 kW';
    if (!p?.value && rangeGranularity === '5min') return '0 kW';
    if (!p?.value && rangeGranularity === '15min') return '0 kW';
    if (!p?.value) return '0 kWh';

    const value = Number(p.value);
    const fixedValue = +value.toFixed(2);
    const isBatteryPower = p.name === 'Battery Power';

    if (
      rangeGranularity === '5min' ||
      rangeGranularity === '15min' ||
      rangeGranularity === '1min'
    ) {
      if (value < 1000) return `${Math.abs(value).toFixed(2)} kW`;

      return `${Math.abs(value / 1000).toFixed(2)} MW`;
    }

    if (value < 1000) {
      return isBatteryPower ? `${fixedValue} kWh` : `${Math.abs(fixedValue)} kWh`;
    }

    const megaWattValue = (value / 1000).toFixed(2);
    return isBatteryPower ? `${megaWattValue} MWh` : `${Math.abs(Number(megaWattValue))} MWh`;
  };

  return (
    <div className="flex flex-col border border-gray-300 bg-white p-4 text-base">
      <p>{formattedDate}</p>
      <div className="mt-2">
        {payload?.map((p: Payload<ValueType, NameType>) => (
          <p style={{ color: p.stroke }} key={p.name}>
            {p.name} : {formatValue(p)}
          </p>
        ))}
      </div>
    </div>
  );
};

function formatDate(
  date: Date,
  rangeGranularity?: RangeGranularity,
  dateDifference?: {
    days: number;
    weeks: number;
    months: number;
    years: number;
  },
  locale?: Locale
): string {
  if (!rangeGranularity || !dateDifference) return '';

  const capitalizeWords = (str: string) =>
    str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const { days, weeks, months, years } = dateDifference;

  if (
    rangeGranularity === '1min' ||
    rangeGranularity === '5min' ||
    rangeGranularity === '15min' ||
    rangeGranularity === 'hourly'
  ) {
    if ((isToday(date) && days <= 1) || (isTomorrow(date) && days <= 1) || days <= 1) {
      return format(date, 'HH:mm');
    } else {
      return capitalizeWords(format(date, 'MMM d, HH:mm', { locale }));
    }
  } else if (rangeGranularity === 'daily') {
    if (weeks < 2) {
      return capitalizeWords(format(date, 'EEE, MMM d', { locale }));
    } else if (months < 2) {
      return capitalizeWords(format(date, 'MMM d', { locale }));
    } else if (isThisYear(date)) {
      return capitalizeWords(format(date, 'MMM d', { locale }));
    } else {
      return capitalizeWords(format(date, 'MMM d, yyyy', { locale }));
    }
  } else if (rangeGranularity === 'monthly') {
    if (years < 1) {
      return capitalizeWords(format(date, 'MMM', { locale }));
    } else {
      return capitalizeWords(format(date, 'MMM yyyy', { locale }));
    }
  }

  // Default fallback
  return capitalizeWords(format(date, 'yyyy-MM-dd HH:mm', { locale }));
}
